import {
  Grid,
  Select,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { regionApi } from "apis";
import { partnerApi } from "apis/partnerApi";
import { CreatableSelect, Loading } from "components";
import { SelectOption } from "components/ui/CreatableSelect/CreatableSelect";
import CustomText from "components/ui/CustomText/CustomText";
import { RegionResponse } from "constants/types/region";
import { FormikProps, getIn } from "formik";
import { memo, useCallback, useRef, useState } from "react";
import { getInitials } from "utils/helpers/string";
import { useNotification } from "utils/hooks";
import { useSuppliers } from "utils/hooks/request/usePartners";
import { useListRegion } from "utils/hooks/request/useRegions";
import { useUsers } from "utils/hooks/request/useUsers";
import { POCreateFormValues, POPartnerFormValues, emptyPartner } from "../../config";

const spanHalf = { base: 12, md: 6 };

type POPartnerField = keyof POPartnerFormValues;

interface PartnerPickerFieldProps {
  partnerId: string;
  options: SelectOption[] | undefined;
  error: string | null;
  onSelect: (partnerId: string | null) => void;
  onBlur: () => void;
}

const PartnerPickerField = memo(
  ({ partnerId, options, error, onSelect, onBlur }: PartnerPickerFieldProps) => (
    <Select
      label="Chọn nhà cung cấp có sẵn"
      placeholder="Tìm kiếm nhà cung cấp..."
      searchable
      clearable
      data={options ?? []}
      value={partnerId || null}
      onChange={onSelect}
      onBlur={onBlur}
      error={error}
    />
  ),
);
PartnerPickerField.displayName = "PartnerPickerField";

interface PartnerTextFieldProps {
  field: POPartnerField;
  label: string;
  placeholder: string;
  value: string;
  required?: boolean;
  disabled?: boolean;
  error: string | null;
  onFieldChange: (field: POPartnerField, value: string) => void;
  onFieldBlur: (field: POPartnerField) => void;
}

const PartnerTextField = memo(
  ({
    field,
    label,
    placeholder,
    value,
    required,
    disabled,
    error,
    onFieldChange,
    onFieldBlur,
  }: PartnerTextFieldProps) => (
    <TextInput
      label={label}
      placeholder={placeholder}
      withAsterisk={required}
      disabled={disabled}
      value={value}
      onChange={(e) => onFieldChange(field, e.target.value)}
      onBlur={() => onFieldBlur(field)}
      error={error}
    />
  ),
);
PartnerTextField.displayName = "PartnerTextField";

interface PartnerCreatableFieldProps {
  field: POPartnerField;
  label: string;
  placeholder: string;
  value: string;
  required?: boolean;
  disabled?: boolean;
  data: SelectOption[];
  error: string | null;
  allowCreate?: boolean;
  onCreate?: (query: string) => Promise<SelectOption>;
  onFieldChange: (field: POPartnerField, value: string) => void;
  onFieldBlur: (field: POPartnerField) => void;
}

const PartnerCreatableField = memo(
  ({
    field,
    label,
    placeholder,
    value,
    required,
    disabled,
    data,
    error,
    allowCreate,
    onCreate,
    onFieldChange,
    onFieldBlur,
  }: PartnerCreatableFieldProps) => (
    <CreatableSelect
      label={label}
      placeholder={placeholder}
      withAsterisk={required}
      disabled={disabled}
      allowCreate={allowCreate}
      data={data}
      value={value || null}
      onChange={(v) => onFieldChange(field, v ?? "")}
      onBlur={() => onFieldBlur(field)}
      error={error}
      onCreate={onCreate}
    />
  ),
);
PartnerCreatableField.displayName = "PartnerCreatableField";

interface PartnerNotesFieldProps {
  value: string;
  disabled?: boolean;
  error: string | null;
  onFieldChange: (field: POPartnerField, value: string) => void;
}

const PartnerNotesField = memo(
  ({ value, disabled, error, onFieldChange }: PartnerNotesFieldProps) => (
    <Textarea
      label="Ghi chú"
      placeholder="Nhập ghi chú (tuỳ chọn)"
      autosize
      minRows={2}
      disabled={disabled}
      value={value}
      onChange={(e) => onFieldChange("notes", e.target.value)}
      error={error}
    />
  ),
);
PartnerNotesField.displayName = "PartnerNotesField";

interface PurchaseOrderPartnerFieldsProps {
  formik: FormikProps<POCreateFormValues>;
}

const PurchaseOrderPartnerFields = memo(
  ({ formik }: PurchaseOrderPartnerFieldsProps) => {
    const [isFetching, setIsFetching] = useState(false);
    // Đơn MUA thì đối tác là nhà cung cấp. Không dùng useListPartner()
    // (type=both) vì backend hiểu "both" là CHỈ đối tác gắn nhãn both — nhà
    // cung cấp thuần type=supplier sẽ không hiện ra trong danh sách chọn.
    const { supplierOptions: partnerOptions } = useSuppliers();
    const { regionOptions, mutate: mutateRegions } = useListRegion();
    const { userOptions } = useUsers();
    const { notification } = useNotification();

    const partner = formik.values.partner;
    const isExisting = !!partner.id;

    const formikRef = useRef(formik);
    formikRef.current = formik;

    const handlePartnerSelect = useCallback(async (partnerId: string | null) => {
      const f = formikRef.current;
      if (!partnerId) {
        f.setFieldValue("partner", emptyPartner());
        f.setTouched({ ...f.touched, partner: {} as any });
        return;
      }
      setIsFetching(true);
      try {
        const res = await partnerApi.getById(partnerId);
        const p = res.data.data;
        f.setFieldValue("partner", {
          id: partnerId,
          name: p.name ?? "",
          phone: p.phone ?? "",
          taxCode: p.taxCode ?? "",
          contactPersonId: p.contactPersonId ?? "",
          regionId: p.regionId ?? "",
          notes: p.notes ?? "",
        });
      } catch {
        // silently ignore – supplier list is still available for retry
      }
      setIsFetching(false);
    }, []);

    const handlePartnerIdBlur = useCallback(() => {
      formikRef.current.setFieldTouched("partner.id", true);
    }, []);

    const handleFieldChange = useCallback(
      (field: POPartnerField, value: string | boolean) => {
        formikRef.current.setFieldValue(`partner.${field}`, value);
      },
      [],
    );

    const handleFieldBlur = useCallback((field: POPartnerField) => {
      formikRef.current.setFieldTouched(`partner.${field}`, true);
    }, []);

    const handleRegionCreate = useCallback(
      async (query: string) => {
        try {
          const res = await regionApi.create({
            name: query,
            code: getInitials(query),
          });
          const data: RegionResponse = res.data.data;
          notification.success({
            title: `Tạo địa bàn "${query}" thành công`,
          });
          mutateRegions();
          return { label: data.name, value: data.id };
        } catch (error: any) {
          notification.error({
            title: "Tạo địa bàn thất bại",
            message: error?.error?.message,
          });
          throw error;
        }
      },
      [notification, mutateRegions],
    );

    const fieldError = (field: string) => {
      if (isExisting) return null;
      const touched = getIn(formik.touched, `partner.${field}`);
      const error = getIn(formik.errors, `partner.${field}`);
      return touched && error ? (error as string) : null;
    };

    const idError = (() => {
      const touched = getIn(formik.touched, "partner.id");
      const error = getIn(formik.errors, "partner.id");
      return touched && error ? (error as string) : null;
    })();

    return (
      <Stack gap="sm" pos="relative">
        <CustomText variant="section-title">Thông tin nhà cung cấp</CustomText>
        <Loading visible={isFetching} variant="overlay" />

        <PartnerPickerField
          partnerId={partner.id}
          options={partnerOptions}
          error={idError}
          onSelect={handlePartnerSelect}
          onBlur={handlePartnerIdBlur}
        />

        <Grid>
          <Grid.Col span={spanHalf}>
            <PartnerTextField
              field="name"
              label="Tên nhà cung cấp"
              placeholder="Nhập tên nhà cung cấp"
              value={partner.name}
              required={!isExisting}
              disabled={isExisting}
              error={fieldError("name")}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <PartnerTextField
              field="phone"
              label="Số điện thoại"
              placeholder="Nhập số điện thoại"
              value={partner.phone}
              disabled={isExisting}
              error={fieldError("phone")}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <PartnerTextField
              field="taxCode"
              label="Mã số thuế"
              placeholder="Nhập mã số thuế"
              value={partner.taxCode}
              required={!isExisting}
              disabled={isExisting}
              error={fieldError("taxCode")}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <PartnerCreatableField
              field="contactPersonId"
              label="Người phụ trách"
              placeholder="Chọn người phụ trách"
              value={partner.contactPersonId}
              disabled={isExisting}
              allowCreate={false}
              data={userOptions ?? []}
              error={fieldError("contactPersonId")}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
            />
          </Grid.Col>
          <Grid.Col span={spanHalf}>
            <PartnerCreatableField
              field="regionId"
              label="Địa bàn"
              placeholder="Chọn địa bàn"
              value={partner.regionId}
              required={!isExisting}
              disabled={isExisting}
              data={regionOptions ?? []}
              error={fieldError("regionId")}
              onCreate={handleRegionCreate}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <PartnerNotesField
              value={partner.notes}
              disabled={isExisting}
              error={fieldError("notes")}
              onFieldChange={handleFieldChange}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    );
  },
  (prev, next) =>
    prev.formik.values.partner === next.formik.values.partner &&
    prev.formik.errors.partner === next.formik.errors.partner &&
    prev.formik.touched.partner === next.formik.touched.partner,
);

PurchaseOrderPartnerFields.displayName = "PurchaseOrderPartnerFields";

export default PurchaseOrderPartnerFields;
